'use client';

import { ArrowRight, Check, Minus } from 'lucide-react';
import { useTranslations } from 'next-intl';

import { MessagingCtaButtons } from '@/components/contact/MessagingCtaButtons';
import { StandardFAQ } from '@/components/faq/StandardFAQ';
import { UnifiedContainer } from '@/components/layout/UnifiedContainer';
import { WindowCleaningCalculator } from '@/components/services/WindowCleaningCalculator';
import { Button } from '@/components/ui/button';
import { Link } from '@/i18n/routing';

const COUNTING_IDS = [
  'single',
  'large',
  'glassDoor',
  'frenchSingle',
  'frenchLarge',
  'area',
] as const;
const INCLUDED_IDS = ['glass', 'frames', 'streaks'] as const;
const EXTRA_IDS = ['blinds', 'renovation', 'access'] as const;
const STEP_IDS = ['choose', 'see', 'book'] as const;

export interface WindowCleaningPageProps {
  startingPrice: string;
  faqTitle: string;
  faqDescription: string;
  faqItems: Array<{ question: string; answer: string }>;
  contactLabel: string;
  whatsappLabel: string;
  whatsappAria: string;
}

export function WindowCleaningPage({
  startingPrice,
  faqTitle,
  faqDescription,
  faqItems,
  contactLabel,
  whatsappLabel,
  whatsappAria,
}: WindowCleaningPageProps) {
  const t = useTranslations('servicesPage.windowCleaningLanding');

  return (
    <>
      <section className='border-b border-oxford-blue/10 bg-seasalt/50'>
        <UnifiedContainer
          size='xl'
          padding='lg'
          className='py-[clamp(2.5rem,4.5vw,4.75rem)]'
        >
          <div className='grid items-end gap-10 lg:grid-cols-12 lg:gap-x-12 lg:gap-y-0'>
            {/* Primary copy — tight internal rhythm */}
            <div className='flex flex-col gap-3 lg:col-span-7 lg:gap-4'>
              <p className='text-xs font-semibold uppercase tracking-[0.16em] text-orange-peel-700'>
                {t('eyebrow')}
              </p>
              <h1 className='max-w-[18ch] text-pretty text-[clamp(2.125rem,4.2vw,3.25rem)] font-extrabold leading-[1.05] tracking-[-0.03em] text-oxford-blue'>
                {t('headline')}
              </h1>
              <p className='max-w-xl text-base leading-relaxed text-oxford-blue/70 sm:text-[1.0625rem]'>
                {t('lead')}
              </p>

              <ul className='mt-3 flex flex-col gap-2 border-t border-oxford-blue/10 pt-5 text-sm text-oxford-blue/80 sm:mt-4 sm:flex-row sm:flex-wrap sm:gap-x-6 sm:gap-y-2'>
                {(['bothSides', 'frames', 'insured'] as const).map(key => (
                  <li key={key} className='inline-flex items-center gap-2'>
                    <Check
                      className='h-4 w-4 shrink-0 text-dark-spring-green-700'
                      aria-hidden
                    />
                    {t(`facts.${key}`)}
                  </li>
                ))}
              </ul>
            </div>

            {/* Conversion cluster — separated, not stacked into the copy */}
            <div className='flex flex-col gap-4 lg:col-span-5 lg:pb-1'>
              <div className='flex flex-col gap-1.5'>
                <p className='text-xs font-semibold uppercase tracking-[0.12em] text-oxford-blue/55'>
                  {t('price.caption')}
                </p>
                <p className='text-[clamp(1.5rem,2.5vw,1.875rem)] font-extrabold tabular-nums leading-none text-orange-peel-700'>
                  {startingPrice}
                </p>
                <p className='max-w-sm text-sm leading-snug text-oxford-blue/60'>
                  {t('price.minimum')}
                </p>
              </div>

              <div className='flex flex-col items-start gap-2.5 pt-1'>
                <Button
                  asChild
                  size='lg'
                  className='h-12 bg-orange-peel px-7 text-base font-bold text-white hover:bg-orange-peel/90'
                >
                  <a href='#window-calculator'>
                    {t('price.cta')}
                    <ArrowRight className='ml-2 h-4 w-4' aria-hidden />
                  </a>
                </Button>
                <Link
                  href='/contact'
                  className='text-sm font-semibold text-oxford-blue/70 underline-offset-4 hover:text-oxford-blue hover:underline'
                >
                  {t('price.secondary')}
                </Link>
              </div>
            </div>
          </div>
        </UnifiedContainer>
      </section>

      <WindowCleaningCalculator />

      <section id='window-scope' className='scroll-mt-24 bg-white py-14 sm:py-20'>
        <UnifiedContainer size='xl' padding='lg'>
          <div className='max-w-2xl'>
            <h2 className='text-3xl font-extrabold tracking-tight text-oxford-blue sm:text-4xl'>
              {t('scope.title')}
            </h2>
            <p className='mt-3 text-base leading-relaxed text-oxford-blue/70'>{t('scope.lead')}</p>
          </div>

          <div className='mt-10 grid gap-10 md:grid-cols-2 md:gap-16'>
            <div>
              <h3 className='text-sm font-bold uppercase tracking-[0.12em] text-dark-spring-green-700'>
                {t('scope.includedTitle')}
              </h3>
              <ul className='mt-4 space-y-4'>
                {INCLUDED_IDS.map(id => (
                  <li key={id} className='flex gap-3'>
                    <Check
                      className='mt-0.5 h-4 w-4 shrink-0 text-dark-spring-green-700'
                      aria-hidden
                    />
                    <span>
                      <span className='block font-semibold text-oxford-blue'>
                        {t(`scope.included.${id}.title`)}
                      </span>
                      <span className='mt-1 block text-sm leading-relaxed text-oxford-blue/70'>
                        {t(`scope.included.${id}.body`)}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className='text-sm font-bold uppercase tracking-[0.12em] text-oxford-blue/50'>
                {t('scope.extraTitle')}
              </h3>
              <ul className='mt-4 space-y-4'>
                {EXTRA_IDS.map(id => (
                  <li key={id} className='flex gap-3'>
                    <Minus className='mt-0.5 h-4 w-4 shrink-0 text-oxford-blue/40' aria-hidden />
                    <span>
                      <span className='block font-semibold text-oxford-blue'>
                        {t(`scope.extra.${id}.title`)}
                      </span>
                      <span className='mt-1 block text-sm leading-relaxed text-oxford-blue/70'>
                        {t(`scope.extra.${id}.body`)}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </UnifiedContainer>
      </section>

      <section className='border-y border-oxford-blue/8 bg-linen py-14 sm:py-20'>
        <UnifiedContainer size='xl' padding='lg'>
          <div className='grid gap-10 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] lg:gap-16'>
            <div>
              <h2 className='text-3xl font-extrabold tracking-tight text-oxford-blue sm:text-4xl'>
                {t('counting.title')}
              </h2>
              <p className='mt-3 text-base leading-relaxed text-oxford-blue/70'>
                {t('counting.lead')}
              </p>
            </div>
            <dl className='grid gap-3 sm:grid-cols-2 lg:grid-cols-3'>
              {COUNTING_IDS.map(id => (
                <div
                  key={id}
                  className='flex items-center justify-between gap-3 rounded-xl border border-oxford-blue/10 bg-seasalt/40 px-4 py-3 transition-colors hover:border-oxford-blue/25 hover:bg-white'
                >
                  <dt className='text-sm font-semibold text-oxford-blue sm:text-base'>
                    {t(`counting.rows.${id}.label`)}
                  </dt>
                  <dd className='shrink-0 rounded-lg bg-oxford-blue px-3 py-1 text-sm font-extrabold tabular-nums text-seasalt'>
                    {t(`counting.rows.${id}.value`)}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </UnifiedContainer>
      </section>

      <section className='bg-white py-14 sm:py-16'>
        <UnifiedContainer size='xl' padding='lg'>
          <h2 className='max-w-xl text-3xl font-extrabold tracking-tight text-oxford-blue sm:text-4xl'>
            {t('steps.title')}
          </h2>
          <ol className='mt-8 grid gap-6 md:grid-cols-3'>
            {STEP_IDS.map((id, index) => (
              <li key={id} className='border-t-2 border-oxford-blue pt-4'>
                <p className='text-xs font-bold tabular-nums text-orange-peel'>0{index + 1}</p>
                <h3 className='mt-2 text-lg font-bold text-oxford-blue'>
                  {t(`steps.items.${id}.title`)}
                </h3>
                <p className='mt-2 text-sm leading-relaxed text-oxford-blue/70'>
                  {t(`steps.items.${id}.body`)}
                </p>
              </li>
            ))}
          </ol>
        </UnifiedContainer>
      </section>

      <StandardFAQ
        title={faqTitle}
        description={faqDescription}
        faqItems={faqItems}
        sectionHeadingLevel='secondary'
      />

      <section className='bg-oxford-blue py-14 text-seasalt sm:py-16'>
        <UnifiedContainer
          size='xl'
          padding='lg'
          className='flex flex-col gap-6 md:flex-row md:items-end md:justify-between'
        >
          <div className='max-w-xl'>
            <h2 className='text-3xl font-extrabold tracking-tight sm:text-4xl'>
              {t('close.title')}
            </h2>
            <p className='mt-3 text-base leading-relaxed text-seasalt/75'>{t('close.lead')}</p>
          </div>
          <div className='flex flex-col items-start gap-3'>
            <Button
              asChild
              size='lg'
              className='h-12 bg-orange-peel px-6 text-base font-bold text-white hover:bg-orange-peel/90'
            >
              <a href='#window-calculator'>
                {t('close.cta')}
                <ArrowRight className='ml-2 h-4 w-4' aria-hidden />
              </a>
            </Button>
            <MessagingCtaButtons
              whatsappLabel={whatsappLabel}
              whatsappAria={whatsappAria}
              buttonClassName='border-white/30 bg-transparent text-seasalt hover:bg-white hover:text-oxford-blue'
            />
            <Link
              href='/contact'
              className='text-sm font-semibold text-seasalt/80 underline-offset-4 hover:underline'
            >
              {contactLabel}
            </Link>
          </div>
        </UnifiedContainer>
      </section>
    </>
  );
}
